// All Gemini calls go through /api/gemini (Vercel serverless) — the key never ships to the browser.
async function callGemini(payload) {
  const res = await fetch("/api/gemini", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`gemini api ${res.status}`);
  const data = await res.json();
  return data.text || "";
}

/**
 * One Kenneth chat turn. `history` is the full prior conversation
 * [{role:'user'|'model', text}] (raw model text, tokens included).
 * @returns {Promise<string>} raw model reply
 */
export function chatTurn(history, message, advisorName = "Kenneth") {
  return callGemini({ action: "chat", history, message, advisorName });
}

export const getAdvisorPrompt = (name = 'Kenneth') => `You are ${name}, a seasoned automotive service advisor. Your job is to gather information from customers about the concerns they are having with their vehicle. We need as much detail as possible but we are not diagnosing in this setting. Simply collect the information from the customer by listening to their concern and then gathering relevant information to help the technician pinpoint the cause. Only ask one question at a time. Remember, most folks are car stupid and we don't want to overwhelm them. Let the answer to each question inform the next. Never offer to look anything up. Your only job is to collect information.

Start by warmly introducing yourself as ${name} and asking the customer for the reason for their visit today.

VEHICLE IDENTIFICATION — CRITICAL, must happen in your second reply:
Your first message is your greeting and reason-for-visit question. In your SECOND reply (after the customer describes their concern) you MUST identify the vehicle. Do it this way:

1. Guide the customer to look at the sticker on the inside of the driver's side door frame — the door jamb — and tap the scan button to photograph the VIN. Include the token [SCAN_VIN] in that message to show the scan button.
2. If the customer says they cannot scan (bad lighting, no camera, etc.), respond with the token [VEHICLE_FORM] to bring up a form where they can select year, make, and model from a list. Do NOT ask them to type the year/make/model in chat — always use [VEHICLE_FORM] for that fallback.
3. Engine size is helpful but not required. If the customer mentions it naturally, note it. Do not block progress waiting for it.
4. Do not move on to deeper service questions until you have received either a successful VIN scan result or a vehicle form submission in the chat. Wait for it.

Once the vehicle is confirmed, dig into the concern in detail: symptoms, when and how often it happens, how long it has been going on, severity, and any recent repairs or service history.

When you have a thorough picture, politely wrap up, thank the client warmly by their first name, and end your final message with [DONE] as the very last characters.`;

/**
 * Generate a technician-ready repair order summary from a Kenneth chat transcript.
 * @param {Array<{role: string, text: string}>} transcript
 * @returns {Promise<string>}
 */
export async function generateTechSummary(transcript) {
  return callGemini({ action: "summary", transcript });
}

/** Downscale to <=1280px JPEG so the upload stays well under Vercel's 4.5MB body limit. */
function fileToBase64(file, maxDim = 1280) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85).split(",")[1]);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });
}

/**
 * Decode a VIN using the NHTSA free API.
 * Returns year, make, model, trim, and a formatted engine string — or null on failure.
 *
 * @param {string} vin
 * @returns {Promise<{ year: number, make: string, model: string, trim: string|null, engine: string|null } | null>}
 */
export async function decodeVin(vin) {
  try {
    const res = await fetch(
      `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${encodeURIComponent(vin)}?format=json`
    );
    if (!res.ok) return null;
    const data = await res.json();
    const results = data.Results || [];

    const getValue = (variable) =>
      results.find((r) => r.Variable === variable)?.Value || null;

    const year = getValue("Model Year");
    const make = getValue("Make");
    const model = getValue("Model");
    const trim = getValue("Trim");

    // Build engine string the same way lv-auto2 does
    const displacement = getValue("Displacement (L)");
    const config = getValue("Engine Configuration");
    const cylinders = getValue("Engine Cylinders");
    const engine = [
      displacement ? `${displacement}L` : null,
      config,
      cylinders ? `${cylinders} cyl` : null,
    ]
      .filter(Boolean)
      .join(" ") || null;

    if (!year || !make || !model) return null;

    return {
      year: parseInt(year),
      make,
      model,
      trim: trim || null,
      engine: engine || null,
    };
  } catch {
    return null;
  }
}

/**
 * Scan a photo with Gemini and extract the VIN or plate number.
 * For VIN, also calls NHTSA to decode vehicle data.
 *
 * @param {File} file - The image file from the camera
 * @param {"vin" | "plate"} type
 * @returns {Promise<{ value: string, vehicleData: object | null }>}
 */
export async function scanImage(file, type) {
  const base64 = await fileToBase64(file);
  const value = await callGemini({ action: "scan", type, image: base64, mimeType: "image/jpeg" });

  if (type === "vin") {
    const vehicleData = await decodeVin(value);
    return { value, vehicleData };
  }

  return { value, vehicleData: null };
}
